import React, { useRef, useState } from "react";
import LandingPageVideo from "../../../assets/videos/CompressedLanding.mp4";
import LandingPagePoster from "../../../assets/images/LandingPoster.webp";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollSmoother } from "gsap/ScrollSmoother";
import { useGSAP } from "@gsap/react";
import Header from "../../../components/Header";
import { splitTextIntoSpans } from "../../../utils/gsapAnimations";

import "./Hero.scss";

//import icons
import { FaArrowRight } from "react-icons/fa6";

// Register ScrollTrigger and ScrollSmoother plugins
gsap.registerPlugin(ScrollTrigger, ScrollSmoother,useGSAP);

export default function Hero({ onVideoReady, onContact }) {
  const videoRef = useRef(null);
  const subtitleContainerRef = useRef(null);
  const descTextRef = useRef(null);
  const buttonsRef = useRef(null);
  const titleBgRef = useRef(null);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const hasCalledVideoReady = useRef(false);

  // new: expose smoother instance to pass to Header
  const [smootherInstance, setSmoootherInstance] = useState(null);

  // Helper to detect mobile devices
  const isMobile = () => window.innerWidth <= 768;

  useGSAP(() => {
    
    // Determine device once for this effect (avoid shadowing the helper)
    const mobile = window.innerWidth <= 768;

    // Initialize ScrollSmoother only on non-mobile for performance
    let localSmoother = null;
    if (!mobile) {
      localSmoother = ScrollSmoother.create({
        wrapper: "#smooth-wrapper",
        content: "#smooth-content",
        smooth: 1.5,
        effects: true,
        smoothTouch: 2,
      });
      // store instance so we can pass it to Header
      setSmoootherInstance(localSmoother);
    }

    if (
      videoRef.current &&
      subtitleContainerRef.current &&
      descTextRef.current &&
      buttonsRef.current &&
      titleBgRef.current
    ) {
      // Start with full screen (no clip path)
      gsap.set(videoRef.current, {
        clipPath: mobile
          ? "inset(0% 0% 0% 0% round 0px)"
          : "inset(0% 0% 0% 0% round 0px)",
      });

      const subtitleSpans =
        subtitleContainerRef.current.querySelectorAll("span");
      const descSpans = descTextRef.current.querySelectorAll("span");

      // Create a timeline for the video animation
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: videoRef.current.parentElement,
          start: "top top",
          end: mobile ? "+=100%" : "+=160%", // Shorter scroll distance for mobile
          scrub: 1,
          pin: true,
          pinSpacing: true,
          preventOverlaps: true,
          fastScrollEnd: true,
        },
        onComplete: () => {
          console.log("Animation complete - can scroll to next section");
        },
      });

      // Use shorter clipPath height for mobile
      const clipPathValue = mobile
        ? "inset(12% 5% 50% 5% round 20px)"
        : "inset(12% 5% 30% 5% round 20px)";

      // Add the clip path animation to the timeline
      tl.to(videoRef.current, {
        clipPath: clipPathValue,
        ease: "power1.inOut",
        duration: mobile ? 1.2 : 3, // Faster animation for mobile
      });

      // On mobile, animate title background to follow clip path
      if (mobile) {
        tl.to(
          titleBgRef.current,
          {
            top: "50%",
            ease: "power1.inOut",
            duration: 1, // Reduced from 2.5
            delay: 0.4, // Reduced from 0.9
          },
          0
        );
      }

      // Animate subtitle spans
      tl.fromTo(
        subtitleSpans,
        {
          x: "-100%",
          opacity: 0,
          // only add blur on non-mobile
          ...(!mobile && { filter: "blur(10px)" }),
        },
        {
          x: "0%",
          opacity: 1,
          // only add filter transition on non-mobile
          ...(!mobile && { filter: "blur(0px)" }),
          duration: mobile ? 0.5 : 0.8, // Faster for mobile
          ease: "power1.inOut",
          stagger: mobile ? 0.02 : 0.05, // Reduced stagger for mobile
        },
        mobile ? "-=1" : "-=1.2"
      );

      // Animate desc spans after subtitle animation completes
      tl.fromTo(
        descSpans,
        {
          x: "-100%",
          opacity: 0,
          // only add blur on non-mobile
          ...(!mobile && { filter: "blur(10px)" }),
        },
        {
          x: "0%",
          opacity: 1.5,
          // only add filter transition on non-mobile
          ...(!mobile && { filter: "blur(0px)" }),
          duration: mobile ? 0.5 : 1.5, // Faster for mobile
          ease: "power1.inOut",
          stagger: mobile ? 0.002 : 0.005, // Reduced stagger for mobile
        },
        mobile ? "-=0.8" : "-=1.2"
      );

      // Animate buttons at the same time as desc spans
      tl.fromTo(
        buttonsRef.current,
        {
          x: 0,
          y: 30,
          scale: 1.1,
          opacity: 0,
          // only add blur on non-mobile
          ...(!mobile && { filter: "blur(10px)" }),
        },
        {
          x: 0,
          y: 0,
          scale: 1,
          opacity: 1,
          // only add filter transition on non-mobile
          ...(!mobile && { filter: "blur(0px)" }),
          duration: mobile ? 0.8 : 1.5, // Faster for mobile
          ease: "power1.out",
        },
        mobile ? "-=0.5" : "-=1.5"
      );
    }

    // Cleanup function
    return () => {
      if (localSmoother) {
        localSmoother.kill();
      }
    };
  }, []);

  // Handle when video can play through without stopping
  const handleCanPlayThrough = () => {
    if (!isVideoReady && !hasCalledVideoReady.current) {
      setIsVideoReady(true);
      hasCalledVideoReady.current = true;
      if (onVideoReady) {
        console.log("Video is ready");
        onVideoReady();
      }
    }
  };

  // Handle video waiting/buffering
  const handleWaiting = () => {
    setIsVideoReady(false);
  };

  // Use utility function for text splitting
  const subtitleSpans = splitTextIntoSpans(
    "ENSURE YOUR PROPERTY WORKS FOR YOU",
    ""
  );
  const descSpans = splitTextIntoSpans(
    "Manage every aspect of your property profile and update it across all our integrated systems",
    ""
  );

  return (
    <>
      <div className="home-hero-container">
        <Header smoother={smootherInstance} />
        <div ref={videoRef} className="home-hero-video-wrapper">
          <video
            src={LandingPageVideo}
            autoPlay
            poster={LandingPagePoster}
            loop
            muted
            playsInline
            onCanPlayThrough={handleCanPlayThrough}
            onWaiting={handleWaiting}
            className="home-hero-video"
          />
          <div className="home-hero-video-overlay"></div>
          <div className="home-hero-title">
            A new way to manage your rental properties
          </div>
        </div>
        <div ref={titleBgRef} className="home-hero-title-bg">
          <div ref={subtitleContainerRef} className="home-hero-subtitle">
            {subtitleSpans}
          </div>
          A new way to manage your rental properties
        </div>
        <div className="home-hero-desc">
          <div className="text" ref={descTextRef}>
            {descSpans}
          </div>
          <div className="buttons" ref={buttonsRef}>
            <div className="button" onClick={onContact}>
              {"Try today".split("").map((char, i) => (
                <span className="hero-button-letter" key={i}>
                  {char === " " ? "\u00A0" : char}
                </span>
              ))}
            </div>
            <div className="button-two">
              <span className="hero-button-letter">
                <FaArrowRight />
              </span>
            </div>
          </div>
        </div>
      </div>

    </>
  );
}
